import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  ConsignmentFeeCharge,
  ConsignmentFeeChargeDetail,
  ConsignmentFeeChargeMutation,
  ConsignmentFeeChargePreview,
  UnsoldFeeChargeSelection,
} from '@tcvs/shared';
import { useConsignmentApi } from './consignment-api';
import { consignmentErrorMessage, formatMoneyMinor as money } from './consignment-ui';

export function ConsignmentCharges({ consignorId }: { consignorId: string }) {
  return <ChargesWorkspace key={consignorId} consignorId={consignorId} />;
}
function ChargesWorkspace({ consignorId }: { consignorId: string }) {
  const api = useConsignmentApi();
  const [items, setItems] = useState<ConsignmentFeeCharge[]>([]);
  const [cursor, setCursor] = useState<string>();
  const [cardId, setCardId] = useState('');
  const [cycle, setCycle] = useState('');
  const [endedAt, setEndedAt] = useState('');
  const [evidence, setEvidence] = useState('');
  const [service, setService] = useState<'' | UnsoldFeeChargeSelection['serviceLevel']>('');
  const [preview, setPreview] = useState<ConsignmentFeeChargePreview>();
  const [detail, setDetail] = useState<ConsignmentFeeChargeDetail>();
  const [reviewed, setReviewed] = useState(false);
  const [reverseReviewed, setReverseReviewed] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const mounted = useRef(true);
  const lock = useRef(false);
  const receipt = useRef<{ signature: string; requestId: string }>();
  const reload = useCallback(
    async (more?: string) => {
      const r = await api.listFeeCharges(consignorId, more);
      if (!r.ok) throw Error(consignmentErrorMessage(r));
      if (!mounted.current) return;
      setItems((old) =>
        more
          ? [
              ...old,
              ...r.data.items.filter((i) => !old.some((o) => o.feeChargeId === i.feeChargeId)),
            ]
          : r.data.items,
      );
      setCursor(r.data.cursor);
    },
    [api, consignorId],
  );
  useEffect(() => {
    mounted.current = true;
    void reload().catch((e) => {
      if (mounted.current) setError(e.message);
    });
    return () => {
      mounted.current = false;
    };
  }, [reload]);
  async function run(action: () => Promise<void>) {
    if (lock.current) return;
    lock.current = true;
    setBusy(true);
    setError(undefined);
    try {
      await action();
    } catch (e) {
      if (mounted.current)
        setError(
          `${e instanceof Error ? e.message : 'Operation failed'}. Retry unchanged after an interrupted response.`,
        );
    } finally {
      lock.current = false;
      if (mounted.current) setBusy(false);
    }
  }
  function invalidate() {
    setPreview(undefined);
    setReviewed(false);
    receipt.current = undefined;
  }
  const selection: UnsoldFeeChargeSelection = {
    consignorId,
    cardId: cardId.trim(),
    listingCycleId: cycle.trim(),
    endedAt: endedAt.trim(),
    evidenceReference: evidence.trim(),
    itemKind: 'SINGLE_CARD',
    serviceLevel: service || 'STANDARD',
  };
  const valid =
    !!selection.cardId &&
    !!selection.listingCycleId &&
    !!service &&
    !!selection.evidenceReference &&
    Number.isFinite(Date.parse(endedAt));
  async function mutate(body: ConsignmentFeeChargeMutation) {
    const r = await api.mutate<ConsignmentFeeCharge>(body);
    if (!r.ok) throw Error(consignmentErrorMessage(r));
    if (!mounted.current) return;
    receipt.current = undefined;
    invalidate();
    await reload();
    const d = await api.getFeeCharge(consignorId, r.data.feeChargeId);
    if (!d.ok) throw Error(consignmentErrorMessage(d));
    if (mounted.current) {
      setDetail(d.data);
      setReverseReviewed(false);
      setReason('');
    }
  }
  function requestId(body: object) {
    const signature = JSON.stringify(body);
    if (receipt.current?.signature !== signature)
      receipt.current = { signature, requestId: crypto.randomUUID() };
    return receipt.current.requestId;
  }
  return (
    <section>
      <h2>Reviewed unsold charges</h2>
      <p>
        Book charges only for a confirmed ended listing with no sale. The $2 unsold fee is added to
        listing and selected service fees. Each listing cycle can be charged once.
      </p>
      {error && (
        <p role="alert" className="tcvs-error">
          {error}
        </p>
      )}
      <fieldset disabled={busy} onChange={invalidate}>
        <legend>Unsold listing evidence</legend>
        <label className="tcvs-field">
          Card ID
          <input
            aria-label="Charge card ID"
            value={cardId}
            onChange={(e) => setCardId(e.target.value)}
          />
        </label>
        <label className="tcvs-field">
          Stored marketplace listing ID
          <input
            aria-label="Charge listing cycle"
            value={cycle}
            onChange={(e) => setCycle(e.target.value)}
          />
        </label>
        <label className="tcvs-field">
          Stored ended time (ISO timestamp with timezone)
          <input
            aria-label="Charge ended time"
            value={endedAt}
            onChange={(e) => setEndedAt(e.target.value)}
          />
        </label>
        <label className="tcvs-field">
          Evidence reference
          <input
            aria-label="Charge evidence reference"
            maxLength={500}
            value={evidence}
            onChange={(e) => setEvidence(e.target.value)}
          />
        </label>
        <label className="tcvs-field">
          Service
          <select
            aria-label="Charge service"
            value={service}
            onChange={(e) => setService(e.target.value as typeof service)}
          >
            <option value="">Select agreed service</option>
            <option value="STANDARD">Standard</option>
            <option value="EXPRESS">Express</option>
            <option value="PREMIUM">Premium</option>
          </select>
        </label>
        <button
          disabled={!valid}
          onClick={() =>
            void run(async () => {
              invalidate();
              const r = await api.mutate<ConsignmentFeeChargePreview>({
                operation: 'PREVIEW_UNSOLD_FEE_CHARGE',
                ...selection,
              });
              if (!r.ok) throw Error(consignmentErrorMessage(r));
              if (mounted.current) setPreview(r.data);
            })
          }
        >
          Preview unsold charge
        </button>
      </fieldset>
      {preview && (
        <div>
          <h3>Review charge</h3>
          <p>
            {preview.charge.cardId} · {preview.charge.terms.name} · {preview.charge.serviceLevel}
          </p>
          <ChargeAmounts charge={preview.charge} />
          <p>
            Evidence: {preview.charge.evidenceReference} · Ended {preview.charge.endedAt}
          </p>
          <label>
            <input
              type="checkbox"
              disabled={busy}
              checked={reviewed}
              onChange={(e) => setReviewed(e.target.checked)}
            />
            I reviewed the ended listing, absence of a sale and this exact charge.
          </label>
          <button
            disabled={busy || !reviewed}
            onClick={() =>
              void run(async () => {
                const body = {
                  operation: 'BOOK_UNSOLD_FEE_CHARGE' as const,
                  ...selection,
                  fingerprint: preview.fingerprint,
                  evidenceReviewed: true,
                };
                await mutate({ ...body, requestId: requestId(body) });
              })
            }
          >
            Book reviewed charge
          </button>
        </div>
      )}
      <h3>Charge history</h3>
      {!items.length && <p>No charges loaded.</p>}
      <ul>
        {items.map((i) => (
          <li key={i.feeChargeId}>
            <button
              disabled={busy}
              onClick={() =>
                void run(async () => {
                  const r = await api.getFeeCharge(consignorId, i.feeChargeId);
                  if (!r.ok) throw Error(consignmentErrorMessage(r));
                  if (mounted.current) {
                    setDetail(r.data);
                    setReason('');
                    setReverseReviewed(false);
                  }
                })
              }
            >
              {i.cardId} · {money(i.amountMinor)} · {i.status}
            </button>
          </li>
        ))}
      </ul>
      {cursor && (
        <button disabled={busy} onClick={() => void run(() => reload(cursor))}>
          More charges
        </button>
      )}
      {detail && (
        <section aria-label="Charge detail">
          <h3>{detail.charge.feeChargeId}</h3>
          <p>
            {detail.charge.status} · {detail.charge.listingCycleId} · {detail.charge.terms.name}
          </p>
          <ChargeAmounts charge={detail.charge} />
          <p>Evidence: {detail.charge.evidenceReference}</p>
          <ol>
            {detail.events.map((e) => (
              <li key={e.version}>
                {e.action} · {e.at} · {e.actor}
                {e.reason ? ` · ${e.reason}` : ''}
              </li>
            ))}
          </ol>
          {detail.charge.status === 'BOOKED' && (
            <fieldset disabled={busy}>
              <legend>Reverse reviewed charge</legend>
              <label>
                Reason
                <textarea
                  aria-label="Charge reversal reason"
                  maxLength={500}
                  value={reason}
                  onChange={(e) => {
                    setReason(e.target.value);
                    setReverseReviewed(false);
                  }}
                />
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={reverseReviewed}
                  onChange={(e) => setReverseReviewed(e.target.checked)}
                />
                I reviewed this reversal. Approved statements may need an adjustment.
              </label>
              <button
                disabled={!reverseReviewed || reason.trim().length < 3}
                onClick={() =>
                  void run(async () => {
                    const body = {
                      operation: 'REVERSE_FEE_CHARGE' as const,
                      consignorId,
                      feeChargeId: detail.charge.feeChargeId,
                      version: detail.charge.version,
                      reason: reason.trim(),
                      reviewed: true,
                    };
                    await mutate({ ...body, requestId: requestId(body) });
                  })
                }
              >
                Reverse charge
              </button>
            </fieldset>
          )}
        </section>
      )}
    </section>
  );
}
function ChargeAmounts({
  charge,
}: {
  charge: Pick<ConsignmentFeeCharge, 'components' | 'amountMinor'>;
}) {
  return (
    <p>
      Unsold {money(charge.components.unsoldFeeMinor)} + Listing{' '}
      {money(charge.components.listingFeeMinor)} + Service{' '}
      {money(charge.components.serviceFeeMinor)} = {money(charge.amountMinor)}
    </p>
  );
}
